import React, { CSSProperties, ReactNode, useEffect, useState, useRef, useMemo } from "react";

import {
  BaseTextFieldProps,
  FormHelperText,
  InputAdornment,
  InputBaseComponentProps,
  OutlinedInputProps,
  TextFieldProps,
  Tooltip,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
  PickerChangeHandlerContext,
  DateValidationError,
  TimeValidationError,
} from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import classNames from "classnames";
import { isValid, parseISO, format, isDate, parse } from "date-fns";
import { zonedTimeToUtc } from "date-fns-tz";
import { Calendar } from "react-date-range";
import { FiCalendar } from "react-icons/fi";

import { Error } from "../../../models/error";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { StyledMenu } from "./DateRange/styles";
import useStyles, {
  FormControlCustomized,
  TextFieldCustomized,
  WrapperInfo,
  WrapperLabel,
} from "./styles";
import { getBrandCustom } from "Util/getCustomColor";
import { useAppState } from "../../../context/StateContext";

export interface InputProps extends BaseTextFieldProps {
  name?: string;
  type?: React.InputHTMLAttributes<unknown>["type"];
  accept?: string;
  label?: React.ReactNode;
  error?: boolean;
  infoMessage?: string;
  errorMessage?: string;
  errorMessageClass?: string;
  errorResponse?: Error;
  disabled?: boolean;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
  maxLength?: number;
  onChange?: OutlinedInputProps["onChange"];
  onBlur?: OutlinedInputProps["onBlur"];
  onFocus?: OutlinedInputProps["onFocus"];
  formControlStyle?: CSSProperties;
  min?: string | number | Date;
  max?: string | number | Date;
  step?: number;
  inputProps?: InputBaseComponentProps;
  InputProps?: Partial<OutlinedInputProps> | undefined;
  bordercolor?: string;
  formControlClassName?: string;
  dateButtonSlot?: (params: { onClick: () => void }) => React.ReactNode;
  labelClassName?: string;
  showDateInput?: boolean;
  disableFuture?: boolean;
  disablePast?: boolean;
  inputTextAlign?: "start" | "center" | "end";
  errorBreakLine?: boolean;
  "data-cy"?: string;
  onInputInvalidate?: (value: boolean) => void;
  renderIconInsideLabel?: () => React.ReactNode;
}

const getDate = (value: string | Date | undefined): Date | undefined => {
  if (!value) return undefined;

  if (typeof value === "string") {
    const dateSplitted = value.split("-");

    if (dateSplitted.length === 3) {
      const year = dateSplitted[0];
      const month = dateSplitted[1];
      const day = dateSplitted[2];
      return parse(`${year}-${month}-${day}`, "yyyy-MM-dd", new Date());
    }
  }

  return isDate(value) ? (value as Date) : new Date(value);
};

const getTime = (value: string | undefined): Date | null => {
  if (!value) return null;

  const [hours, minutes] = value.split(":");

  if (hours === undefined || minutes === undefined) return null;

  const date = new Date();
  date.setHours(Number(hours), Number(minutes), 0, 0);

  return date;
};

const TimeTextField = (fieldProps: TextFieldProps) => (
  <TextFieldCustomized {...fieldProps} helperText="" variant="outlined" />
);

const DateTextField = (fieldProps: TextFieldProps) => (
  <TextFieldCustomized {...fieldProps} helperText="" variant="outlined" />
);

const Input = (props: InputProps) => {
  const {
    label,
    error,
    errorMessage,
    errorMessageClass,
    infoMessage = "",
    disabled,
    fullWidth,
    maxLength,
    min,
    max,
    step,
    leftIcon,
    rightIcon,
    formControlClassName,
    formControlStyle,
    labelClassName,
    defaultValue,
    value,
    type,
    onChange,
    disableFuture,
    disablePast,
    inputTextAlign,
    required,
    errorBreakLine = true,
    errorResponse,
    renderIconInsideLabel,
    onInputInvalidate,
    ...rest
  } = props;

  const { currentUser } = useAppState();

  const divRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState<SVGElement | null>(null);
  const open: boolean = !!anchorEl;

  const [dateIsInValid, setDateIsInValid] = useState<boolean>(false);

  const initialDate = type === "date" && typeof value === "string" ? getDate(value) : undefined;
  const [date, setDate] = useState<Date | undefined>(initialDate);

  const initialTime = type === "time" && typeof value === "string" ? getTime(value) : null;
  const [time, setTime] = useState<Date | null>(initialTime);

  const classes = useStyles({ disabled });
  const hasError = error || dateIsInValid || (errorResponse && errorResponse?.field === rest.name);

  const userDatePreference = currentUser?.preferences?.dateFormat;
  const userTimePreference = currentUser?.preferences?.timeFormat;

  const dateFormat = useMemo(() => {
    return userDatePreference === "MM_DD_YYYY" ? "MM/dd/yyyy" : "dd/MM/yyyy";
  }, [userDatePreference]);

  const datePlaceholder = useMemo(() => {
    return userDatePreference === "MM_DD_YYYY" ? "mm/dd/yyyy" : "dd/mm/yyyy";
  }, [userDatePreference]);

  const useAmPm = useMemo(() => {
    return userTimePreference === "12_HOUR";
  }, [userTimePreference]);

  useEffect(() => {
    if (type !== "date") return;

    if (typeof value === "string") {
      const valueSplitted = value.split("-");

      if (valueSplitted.length === 3) {
        const year = valueSplitted[0];
        const month = valueSplitted[1];
        const day = valueSplitted[2];
        const dateObject = parse(`${year}-${month}-${day}`, "yyyy-MM-dd", new Date());

        setDate(dateObject);
        return;
      }
    }

    setDate(undefined);
  }, [value, type]);

  useEffect(() => {
    if (type !== "time") return;

    if (typeof value === "string") {
      const newTime = getTime(value);

      if (newTime && time && format(newTime, "HH:mm") === format(time, "HH:mm")) {
        return;
      }

      setTime(newTime);
      return;
    }

    setTime(null);
  }, [value, type]);

  const renderRequiredLabel = () => {
    if (required) {
      return <span className={classes.required}>{"*"}</span>;
    }

    return null;
  };

  const renderInfoLabel = () => {
    if (!required && infoMessage?.trim().length) {
      return (
        <Tooltip title={infoMessage} placement="right">
          <WrapperInfo>
            <InfoOutlinedIcon className={classes.extraSmallIcon} />
          </WrapperInfo>
        </Tooltip>
      );
    }

    return null;
  };

  const renderLabel = () => {
    if (!label) return null;

    if (!required && infoMessage?.trim().length) {
      return (
        <span className={classNames(classes.label, labelClassName)}>
          <WrapperLabel>
            {label} {renderInfoLabel()}
          </WrapperLabel>

          {renderIconInsideLabel?.()}
        </span>
      );
    }

    return (
      <span className={classNames(classes.label, labelClassName)}>
        <WrapperLabel>
          {label} {renderRequiredLabel()}
        </WrapperLabel>
        {renderIconInsideLabel?.()}
      </span>
    );
  };

  const renderError = () => {
    if (error && errorMessage) {
      return (
        <FormHelperText
          className={classNames(classes.formHelper, errorMessageClass)}
          data-cy="errors"
          style={!errorBreakLine ? { whiteSpace: "nowrap" } : undefined}
        >
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorMessage}
        </FormHelperText>
      );
    }

    if (errorResponse && errorResponse?.field === rest.name) {
      return (
        <FormHelperText
          className={classes.formHelper}
          data-cy="errors"
          style={!errorBreakLine ? { whiteSpace: "nowrap" } : undefined}
        >
          <InfoOutlinedIcon htmlColor="#D92D20" className={classes.iconError} />
          {errorResponse?.message}
        </FormHelperText>
      );
    }

    return null;
  };

  const handleInvalidation = (value: boolean) => {
    if (onInputInvalidate) {
      onInputInvalidate(value);
      return;
    }

    setDateIsInValid(value);
  };

  const getDateFormated = (value: Date) => {
    return format(value, "yyyy-MM-dd");
  };

  const getTimeFormatted = (value: Date) => {
    return format(value, "HH:mm");
  };

  const executeOnChange = (value?: string | null) => {
    onChange?.({
      target: {
        value,
      },
    } as unknown as React.ChangeEvent<HTMLTextAreaElement>);
  };

  const handleOnDateChange = (
    value: Date | null,
    _context: PickerChangeHandlerContext<DateValidationError>
  ) => {
    setDate(
      value ? zonedTimeToUtc(value, Intl.DateTimeFormat().resolvedOptions().timeZone) : undefined
    );

    try {
      handleInvalidation(true);

      if (!value) {
        executeOnChange(null);
        handleInvalidation(false);
        return;
      }

      const dateFormatted = getDateFormated(value);

      if (isValid(parseISO(dateFormatted))) {
        handleInvalidation(false);
        executeOnChange(dateFormatted);
        return;
      }

      handleInvalidation(true);
    } catch (err) {
      handleInvalidation(true);
    }
  };

  const renderDateInput = () => {
    const minDate = min ? new Date(min) : undefined;

    const extraAttrs = {
      "data-cy": rest["data-cy"] as string,
    };

    return (
      <div ref={divRef}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            name={rest?.name}
            disabled={disabled}
            format={dateFormat}
            minDate={minDate}
            value={date}
            disableFuture={disableFuture}
            disablePast={disablePast}
            open={false}
            onOpen={() => {
              setAnchorEl(divRef.current);
            }}
            onChange={handleOnDateChange}
            slotProps={{
              textField(states) {
                return {
                  fullWidth,
                  placeholder: datePlaceholder,
                  error: error || dateIsInValid,
                  disabled,
                  ...states,
                  ...extraAttrs,
                };
              },
            }}
            sx={{ width: "100% !important" }}
            slots={{
              openPickerIcon: () => <FiCalendar className={classes.keyboardDatepickerIcon} />,
              textField: DateTextField,
            }}
          />
        </LocalizationProvider>

        <StyledMenu
          id="date-range-picker"
          anchorEl={anchorEl}
          open={open}
          onClose={() => setAnchorEl(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          transformOrigin={{ vertical: "top", horizontal: "left" }}
        >
          <Calendar
            showDateDisplay={false}
            preventSnapRefocus
            minDate={min ? (isDate(min) ? (min as Date) : new Date(min)) : undefined}
            maxDate={max ? (isDate(max) ? (max as Date) : new Date(max)) : undefined}
            date={date}
            dateDisplayFormat={dateFormat}
            onChange={(date) => {
              handleInvalidation(false);
              setDate(date);

              executeOnChange(getDateFormated(date));

              setAnchorEl(null);
            }}
          />
        </StyledMenu>
      </div>
    );
  };

  const renderTimeInput = () => {
    return (
      <div ref={divRef} style={{ width: "100%" }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <TimePicker
            name={rest?.name}
            disabled={disabled}
            value={time}
            ampm={useAmPm}
            views={["hours", "minutes"]}
            onChange={(value) => {
              setTime(value);
              if (value && isValid(value)) {
                const timeFormatted = format(value, "HH:mm");
                executeOnChange(timeFormatted);
              }
            }}
            slotProps={{
              popper: {
                anchorEl: divRef.current,
                placement: "bottom-start",
                disablePortal: true,
              },
              textField: (states) => ({
                fullWidth,
                error: hasError,
                disabled,
                ...states,
              }),
            }}
            sx={{ width: "100%" }}
            slots={{
              textField: TimeTextField,
            }}
          />
        </LocalizationProvider>
      </div>
    );
  };

  const renderTextInput = () => {
    return (
      <TextFieldCustomized
        fullWidth={fullWidth}
        defaultValue={!value && value !== "" && defaultValue ? defaultValue : undefined}
        bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", "#308CDB")}
        value={value || ""}
        variant="outlined"
        inputProps={{
          ...props.inputProps,
          autoComplete: props.inputProps?.autoComplete ?? "off",
          maxLength,
          min,
          max,
          step,
          style: {
            textAlign: rest.style?.textAlign ? rest.style?.textAlign : inputTextAlign,
          },
        }}
        onKeyDown={(event: any) => type === "number" && event.key === "e" && event.preventDefault()}
        onChange={onChange}
        InputProps={{
          startAdornment: leftIcon ? (
            <InputAdornment position="start">{leftIcon}</InputAdornment>
          ) : undefined,
          endAdornment: rightIcon ? (
            <InputAdornment position="end">{rightIcon}</InputAdornment>
          ) : undefined,
        }}
        disabled={disabled}
        error={hasError}
        type={type}
        {...rest}
      />
    );
  };

  const renderInputType = () => {
    switch (type) {
      case "date":
        return renderDateInput();
      case "time":
        return renderTimeInput();
      default:
        return renderTextInput();
    }
  };

  return (
    <FormControlCustomized
      fullWidth={fullWidth}
      style={formControlStyle}
      className={formControlClassName}
      disabled={disabled}
      error={hasError}
      bordercolor={error ? "#D92D20" : getBrandCustom("primaryColor", "#308CDB")}
    >
      {renderLabel()}
      {renderInputType()}
      {renderError()}
    </FormControlCustomized>
  );
};

export default Input;
